/**
 * API методы для чата (Dify Integration)
 */

import { AxiosInstance } from 'axios';
import {
  ChatMessageRequest,
  ChatCompletionResponse,
  SuggestedQuestionsResponse,
  AppParametersResponse,
} from '@/types/api.types';

/**
 * Chat API Service
 */
export class ChatApiService {
  constructor(private client: AxiosInstance) {}

  /**
   * Отправляет сообщение в чат (blocking mode)
   * 
   * @description Используется для тестирования. 
   * В production используйте sendMessageStreaming
   * 
   * @param request - данные запроса
   * @returns ответ от API
   */
  async sendMessage(request: ChatMessageRequest): Promise<ChatCompletionResponse> {
    const response = await this.client.post<ChatCompletionResponse>('/chat-messages', request);
    return response.data;
  }

  /**
   * Отправляет сообщение с streaming (SSE)
   * 
   * @description Возвращает конфигурацию для fetch streaming
   * 
   * @param request - данные запроса
   * @returns объект с url и options для fetch
   */
  getStreamingConfig(request: ChatMessageRequest): {
    url: string;
    options: RequestInit;
  } {
    const baseURL = this.client.defaults.baseURL || '';
    const apiKey = (this.client.defaults.headers['Authorization'] as string) || '';

    return {
      url: `${baseURL}/chat-messages`,
      options: {
        method: 'POST',
        headers: {
          'Authorization': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    };
  }

  /**
   * Останавливает генерацию ответа
   * 
   * @param taskId - ID задачи из streaming response
   * @param userId - ID пользователя
   * @returns результат остановки
   */
  async stopGeneration(taskId: string, userId: string): Promise<{ result: string }> {
    const response = await this.client.post(`/chat-messages/${taskId}/stop`, {
      user: userId,
    });
    return response.data;
  }

  /**
   * Получает suggested questions для сообщения
   * 
   * @param messageId - ID сообщения
   * @param userId - ID пользователя
   * @returns массив быстрых вопросов
   */
  async getSuggestedQuestions(
    messageId: string,
    userId: string
  ): Promise<SuggestedQuestionsResponse> {
    const response = await this.client.get<SuggestedQuestionsResponse>(
      `/messages/${messageId}/suggested`,
      {
        params: { user: userId },
      }
    );
    return response.data;
  }

  /**
   * Получает параметры приложения
   * 
   * @description Используется при инициализации для получения:
   * - opening_statement
   * - suggested_questions
   * - file_upload настройки
   * 
   * @returns параметры приложения
   */
  async getParameters(): Promise<AppParametersResponse> {
    const response = await this.client.get<AppParametersResponse>('/parameters');
    return response.data;
  }

  /**
   * Получает информацию о приложении
   * 
   * @returns информация о приложении
   */
  async getInfo(): Promise<{
    name: string;
    description: string;
    tags: string[];
    mode: string;
  }> {
    const response = await this.client.get('/info');
    return response.data;
  }

  /**
   * Получает историю сообщений
   * 
   * @param conversationId - ID беседы
   * @param userId - ID пользователя
   * @param limit - количество сообщений
   * @param firstId - ID первого сообщения (для пагинации)
   * @returns список сообщений
   */
  async getMessages(
    conversationId: string,
    userId: string,
    limit: number = 20,
    firstId?: string
  ): Promise<{
    data: unknown[];
    has_more: boolean;
    limit: number;
  }> {
    const response = await this.client.get('/messages', {
      params: {
        conversation_id: conversationId,
        user: userId,
        limit,
        first_id: firstId,
      },
    });
    return response.data;
  }

  /**
   * Отправляет feedback на сообщение
   * 
   * @param messageId - ID сообщения
   * @param userId - ID пользователя
   * @param rating - оценка (like/dislike)
   * @param content - текст feedback (опционально)
   * @returns результат
   */
  async sendFeedback(
    messageId: string,
    userId: string,
    rating: 'like' | 'dislike' | null,
    content?: string
  ): Promise<{ result: string }> {
    const response = await this.client.post(`/messages/${messageId}/feedbacks`, {
      rating,
      user: userId,
      content,
    });
    return response.data;
  }
}

